/**
 * The work queue: one chosen task, and the work it has left.
 *
 * This store used to hold a filter. That was the conflation the split undid — `#/queue` was
 * simultaneously a corpus browser (seven filter controls over every record) and a work
 * dispatcher, and the give-away was a progress meter that never moved when the filters under
 * it did. Filtering now lives in `browse.svelte.ts`; what lives here is a **task** and the
 * ordered list of what is left of it.
 *
 * The task id is not enumerated in this file, and should not be. `GET /api/tasks` is the
 * registry; a client-side copy of it would be a second source of truth free to drift from the
 * server's done-predicates. What the client owns is the *view* each task is performed in —
 * see `taskViews.ts`.
 *
 * It also holds enough of the list for "complete this image and advance" to know where to go
 * next. That is a workflow requirement, not a caching optimisation: a shortcut that completes
 * an image and then dumps the annotator back on a list they have to re-find their place in is
 * a shortcut nobody uses.
 */

import { api } from '../api/client';
import { asProblem, type ApiProblem } from '../api/problem';
import type { TaskCard, TaskProgress, TaskQueueItem, TaskQueuePage } from '../api/types';
import { nextUnfinishedIndex } from '../nav';
import { itemReasons } from '../taskViews';

const PAGE_SIZE = 50;

/** A record still owes this task work. The queue only contains such records; belt and braces. */
export function hasWorkLeft(item: TaskQueueItem): boolean {
  return item.pending_images > 0;
}

class QueueState {
  /** The chooser's cards. Loaded once and refreshed after work lands, never polled. */
  cards = $state<TaskCard[] | null>(null);
  cardsLoading = $state(false);
  cardsProblem = $state<ApiProblem | null>(null);

  /** The task being worked, or null on the chooser. */
  taskId = $state<string | null>(null);
  page = $state<TaskQueuePage | null>(null);
  offset = $state(0);
  loading = $state(false);
  problem = $state<ApiProblem | null>(null);

  get items(): TaskQueueItem[] {
    return this.page?.items ?? [];
  }

  get total(): number {
    return this.page?.total ?? 0;
  }

  get pageSize(): number {
    return PAGE_SIZE;
  }

  /**
   * The active task's card, preferring the copy the queue page returned.
   *
   * The queue response carries its own card so the numbers beside the list are the numbers
   * that list was computed from — a card read from a chooser fetch minutes ago could disagree
   * with the rows under it, and a progress bar that disagrees with the list beside it is the
   * exact failure this whole change is undoing.
   */
  get task(): TaskCard | null {
    if (this.page && this.page.task.id === this.taskId) return this.page.task;
    return this.cards?.find((card) => card.id === this.taskId) ?? null;
  }

  get progress(): TaskProgress | null {
    return this.task?.progress ?? null;
  }

  async loadCards(): Promise<void> {
    this.cardsLoading = true;
    this.cardsProblem = null;
    try {
      this.cards = (await api.tasks()).tasks;
    } catch (error) {
      this.cardsProblem = asProblem(error);
    } finally {
      this.cardsLoading = false;
    }
  }

  async ensureCards(): Promise<void> {
    if (this.cards || this.cardsLoading) return;
    await this.loadCards();
  }

  /**
   * Work this task, from the top.
   *
   * Selecting the task the store already holds keeps the page rather than resetting the
   * annotator's position: arriving back on `#/queue/<task>` after finishing an image should
   * not scroll them to the start of the queue.
   */
  async select(taskId: string): Promise<void> {
    if (this.taskId === taskId && this.page) return;
    this.taskId = taskId;
    this.offset = 0;
    this.page = null;
    await this.load();
  }

  async load(): Promise<void> {
    const taskId = this.taskId;
    // Nothing to load on the chooser, and callers should not have to know that. `nav` and the
    // workspace both call this speculatively on mount.
    if (!taskId) return;
    this.loading = true;
    this.problem = null;
    try {
      const page = await api.taskQueue(taskId, { limit: PAGE_SIZE, offset: this.offset });
      // Guard against a slow response for a task the annotator has already navigated away
      // from: applying it would put another task's rows under this task's heading.
      if (this.taskId !== taskId) return;
      this.page = page;
      this.syncCard(page.task);
    } catch (error) {
      this.problem = asProblem(error);
    } finally {
      this.loading = false;
    }
  }

  async goTo(offset: number): Promise<void> {
    this.offset = Math.max(0, offset);
    await this.load();
  }

  /** Keep the chooser's copy of this card in step with the one the queue just returned. */
  private syncCard(card: TaskCard): void {
    if (!this.cards) return;
    this.cards = this.cards.map((existing) => (existing.id === card.id ? card : existing));
  }

  /**
   * The next record after `id` **with work left**, for complete-and-advance.
   *
   * The walk itself lives in `lib/nav.ts` next to the pager's positional step, so the one
   * difference between dispatch and navigation — this one skips finished records, the pager
   * does not — is visible in a single file rather than inferred from two copies of a loop.
   * The predicate is the task's, not boxing's: an image with every letter boxed is finished
   * for one task and untouched for the other.
   */
  nextAfter(inscriptionId: number): TaskQueueItem | null {
    const items = this.items;
    const index = items.findIndex((item) => item.id === inscriptionId);
    const next = nextUnfinishedIndex(items, index, hasWorkLeft);
    return next < 0 ? null : (items[next] ?? null);
  }

  /**
   * The image the next queue item opens — the server's choice — for a record other than this one.
   *
   * Complete-and-advance for a task whose work is on finished photographs (`second-pass`),
   * where `nextAfter` + "find an open image" never finds one. Walks forward from the current
   * record when it is on this page; when it is not (opened from the browser), the first row
   * that is not it. Null when nothing is left on the page, which sends the annotator back to
   * the queue to reload.
   */
  nextImageAfter(inscriptionId: number): number | null {
    const items = this.items;
    const index = items.findIndex((item) => item.id === inscriptionId);
    const ahead = index >= 0 ? items.slice(index + 1) : items;
    const next = ahead.find(
      (item) => item.id !== inscriptionId && hasWorkLeft(item) && item.next_image_id !== null,
    );
    return next?.next_image_id ?? null;
  }

  /**
   * Why the active task holds this record, as the queue last reported it. Empty when the record
   * is not on the loaded page, or the task gives no reasons.
   */
  reasonsFor(inscriptionId: number | null | undefined): string[] {
    if (inscriptionId === null || inscriptionId === undefined) return [];
    return itemReasons(this.items.find((item) => item.id === inscriptionId));
  }
}

export const queue = new QueueState();
